import React, { ChangeEvent, FC, HTMLAttributes, useState, useEffect } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import { Card, Counter, ImageBox } from '@engyalo/storefront-components'
import { addProduct, removeProduct, updateQuantity, viewProduct } from '@engyalo/storefront-events'
import { Hook } from '@engyalo/storefront-types'
import { Box, Heading, Skeleton } from '@chakra-ui/core'
import { Price } from '../Price'
import { ICartDictionary, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { useSetRecoilState, useRecoilValue, useRecoilCallback } from 'recoil'
import { cartState, configState, orderConfigState } from '../../recoil/atoms'
import Plugin from '../../Plugin'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import { getActionScreen } from '../../utils/analytics'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'
import { IConfig } from '../../recoil/types'
import { useUnitTypes } from '../../hooks/useUnitTypes'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct
  isLoading: boolean
  showPrice: boolean
}

interface IRunConditionallyParams {
  condition: () => boolean
  after?: () => void
  before?: () => void
}

const runConditionally = ({ condition, after, before }: IRunConditionallyParams) => {
  before?.()

  if (condition()) {
    return
  }

  after?.()
}

export const ProductItem: FC<IProps> = ({ product, isLoading, showPrice }: IProps) => {
  const [cart, setCart] = useState<ICartDictionary>({});
  const setCartState = useSetRecoilState<ICartDictionary>(cartState)
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const { customer, order } = useParams()
  const { unitType, hasUnitTypes } = useUnitTypes(cart, product)
  const totalPrice = getTotalPrice(product)

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
    sessionID: orderConfig.id || '',
  }

  const getProductFirstImage = (product: IProduct): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const firstCartLoad = useRecoilCallback(({snapshot}) => async () => {
    const atomCart = await snapshot.getPromise(cartState);
    const tempCart =  { ...atomCart }
    setCart(tempCart)
    setCartState(tempCart)
  })

  useEffect(() => {
    firstCartLoad()
  }, [])

  const addToCartAsync = useRecoilCallback(({snapshot}) => async (product: IProduct, quantity : number, shouldReplace : boolean) => {
    const atomCart = await snapshot.getPromise(cartState);
    const tempCart =  { ...atomCart }

    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity
          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
            }
            setCart(tempCart)
            setCartState(tempCart)
          } else {
            delete tempCart[currentSku]
            setCart({ ...tempCart })
            setCartState(tempCart)
          }
        }
      } else if (quantity > 0) {
        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity,
          cartId: currentSku,
        } as IProduct
      }
      setCart(tempCart)
      setCartState(tempCart)
    }
  });

  if (!product) {
    return null
  }

  return (
    <Box
      data-qa-node="productitem-container"
      onClick={(): void => {
        if (!config.enabledProductDetail) {
          return
        }

        skipIfInPreviewMode(() => {
          viewProduct({
            product: {
              id: product.id || '',
              sku: product.sku || '',
              name: product.name || '',
              price: product.price || 0,
              category: product.category || '',
              screen: getActionScreen(location),
            },
            session,
          })
        })
        history.push({
          pathname: `/${customer}/${order}/product`,
          state: { product },
          search: location.search,
        })
      }}
    >
      <Plugin
        hook={Hook.COLLECTION_PRODUCT}
        pluginProps={{
          product,
          currency: config.currency,
          currencyPrefix: config.currencyPrefix,
          currencyFormat: config.currencyFormat,
          cart,
          showPrice: showPrice,
          isLoading: isLoading,
        }}
      >
        <Card>
          <Skeleton isLoaded={!isLoading} w="100%" h={isLoading ? '130px' : 'unset'} data-qa-node="productitem-image">
            <ImageBox imageUrl={getProductFirstImage(product)} />
          </Skeleton>
          <Skeleton
            isLoaded={!isLoading}
            w={isLoading ? '40%' : 'unset'}
            h={isLoading ? '16px' : 'unset'}
            flex={isLoading ? 'unset' : '1'}
            marginY="4px"
            data-qa-node="productitem-name"
          >
            <Heading as="h3" size="sm" fontSize="productTitle">
              {product.name}
            </Heading>
          </Skeleton>
          <Skeleton
            isLoaded={!isLoading}
            w={isLoading ? '100%' : 'unset'}
            h={isLoading ? '16px' : 'unset'}
            paddingBottom="4px"
            data-qa-node="productitem-description"
          >
            <Box fontSize="productDescription" fontFamily="body" color="gray.100">
              {product.description}
            </Box>
          </Skeleton>

          {showPrice ? (
            <Skeleton
              isLoaded={!isLoading}
              w={isLoading ? '40%' : 'unset'}
              h={isLoading ? '16px' : 'unset'}
              flex={isLoading ? 'unset' : '1'}
              marginY="4px"
            >
              <Price price={product.price} currency={config.currencyPrefix} currencyFormat={config.currencyFormat} />
            </Skeleton>
          ) : null}

          <Skeleton isLoaded={!isLoading} w="100%" data-qa-node="productitem-counter">
            <Counter
              maxValue={config.maxQuantity}
              name={product.sku || ''}
              addAction={(e: React.MouseEvent<HTMLButtonElement>): void => {
                runConditionally({
                  condition() {
                    return hasUnitTypes
                  },
                  before() {
                    skipIfInPreviewMode(() => {
                      addProduct({
                        product: {
                          id: product.id || '',
                          sku: product.sku || '',
                          name: product.name || '',
                          price: product.price || 0,
                          category: product.category || '',
                          screen: getActionScreen(location),
                          note: '',
                        },
                        session,
                      })
                    })
                  },
                  after() {
                    e.stopPropagation()
                    addToCartAsync(product, 1, false)
                  },
                })
              }}
              minusAction={(e: React.MouseEvent<HTMLButtonElement>): void => {
                runConditionally({
                  condition() {
                    return hasUnitTypes
                  },
                  before() {
                    skipIfInPreviewMode(() => {
                      removeProduct({
                        product: {
                          id: product.id || '',
                          sku: product.sku || '',
                          name: product.name || '',
                          price: product.price || 0,
                          category: product.category || '',
                          screen: getActionScreen(location),
                          note: '',
                        },
                        session,
                      })
                    })
                  },
                  after() {
                    e.stopPropagation()
                    addToCartAsync(product, -1, false)
                  },
                })
              }}
              onInputBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
                runConditionally({
                  condition() {
                    return hasUnitTypes
                  },
                  after() {
                    const quantity = parseInt(event.target.value) || 0
                    skipIfInPreviewMode(() => {
                      updateQuantity({
                        product: {
                          id: product.id || '',
                          sku: product.sku || '',
                          name: product.name || '',
                          category: product.category || '',
                          price: product.price || 0,
                          note: '',
                          quantity,
                          screen: getActionScreen(location),
                        },
                        session,
                      })
                    })
                    addToCartAsync(product, quantity, true)
                  },
                })
              }}
              value={cart[generateCartId(product) || ''] ? cart[generateCartId(product) || ''].quantity : 0}
              format={unitType?.format}
              onInputChange={(event: ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target
                addToCartAsync(product, Number(value), true)
              }}
            />
          </Skeleton>
        </Card>
      </Plugin>
    </Box>
  )
}
